import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ACHIEVEMENTS, getTier, type AchievementTier } from '../data/achievements';
import { type UserProfile } from '../lib/firebase';
import { useAuth } from '../contexts/AuthContext';
import { ref, onValue } from 'firebase/database';
import { db } from '../lib/firebase';

interface UnlockNotification {
    id: string;
    achievementName: string;
    tier: AchievementTier;
    milestone: number;
}

const TIER_METALS: Record<AchievementTier, string> = {
    bronze: 'Bronze',
    silver: 'Silver',
    gold: 'Gold',
    platinum: 'Platinum'
};

const TIER_COLORS: Record<AchievementTier, string> = {
    bronze: '#b87333',
    silver: '#bfc1c2',
    gold: '#ffd700',
    platinum: '#00d2ff'
};

export default function AchievementTracker() {
    const { activeAccount } = useAuth();
    const [notifications, setNotifications] = useState<UnlockNotification[]>([]);
    const lastSeenTiers = useRef<Record<string, AchievementTier | null>>({});
    const isFirstLoad = useRef(true);

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const getStatValue = (key: string, stats: any): number => {
        // Handle dot-notation nested keys (e.g. adventure.bestLevel)
        if (key.includes('.')) {
            const [parent, child] = key.split('.');
            return (stats[parent]?.[child]) || 0;
        }
        switch (key) {
            case 'totalSoloWins_plus_multiplayerWins':
                return (stats.totalSoloWins || 0) + (stats.multiplayerWins || 0);
            case 'totalSoloLosses_plus_multiplayerLosses':
                return (stats.totalSoloLosses || 0) + (stats.multiplayerLosses || 0);
            case 'totalSoloWins_plus_totalSoloLosses':
                return (stats.totalSoloWins || 0) + (stats.totalSoloLosses || 0);
            case 'multiplayerWins_plus_multiplayerLosses':
                return (stats.multiplayerWins || 0) + (stats.multiplayerLosses || 0);
            default:
                return stats[key] || 0;
        }
    };

    useEffect(() => {
        if (!activeAccount || !db) return;

        const userRef = ref(db, `users/${activeAccount.address}`);
        const unsubscribe = onValue(userRef, (snapshot) => {
            if (!snapshot.exists()) return;
            const profile = snapshot.val() as UserProfile;
            const stats = profile.stats;

            ACHIEVEMENTS.forEach(ach => {
                const val = getStatValue(ach.statKey, stats);
                const currentTier = getTier(val, ach.tiers);
                const lastTier = lastSeenTiers.current[ach.id];

                if (!isFirstLoad.current && currentTier && currentTier !== lastTier) {
                    // Check if it's a promotion (higher tier)
                    const tierRank = { null: 0, bronze: 1, silver: 2, gold: 3, platinum: 4 };
                    if (tierRank[currentTier as keyof typeof tierRank] > tierRank[lastTier as keyof typeof tierRank]) {
                        // UNLOCK!
                        const milestone = ach.tiers[currentTier];
                        const newNotif: UnlockNotification = {
                            id: `${ach.id}_${currentTier}_${Date.now()}`,
                            achievementName: ach.name,
                            tier: currentTier,
                            milestone
                        };
                        setNotifications(prev => [...prev, newNotif]);

                        // Auto-remove after 5s
                        setTimeout(() => {
                            setNotifications(prev => prev.filter(n => n.id !== newNotif.id));
                        }, 5000);
                    }
                }
                lastSeenTiers.current[ach.id] = currentTier;
            });

            isFirstLoad.current = false;
        });

        return () => unsubscribe();
    }, [activeAccount]);

    return (
        <div className="fixed bottom-8 right-8 z-[1000] flex flex-col gap-4 pointer-events-none">
            <AnimatePresence>
                {notifications.map((notif) => (
                    <motion.div
                        key={notif.id}
                        initial={{ opacity: 0, x: 100, scale: 0.8 }}
                        animate={{ opacity: 1, x: 0, scale: 1 }}
                        exit={{ opacity: 0, scale: 0.5, x: 50 }}
                        className="glassmorphism p-6 rounded-[2rem] border-t-4 shadow-[0_20px_50px_rgba(0,0,0,0.5)] flex items-center gap-6 pointer-events-auto"
                        style={{ borderTopColor: TIER_COLORS[notif.tier] }}
                    >
                        <div
                            className="w-16 h-16 rounded-2xl flex items-center justify-center text-4xl border-2"
                            style={{
                                background: `linear-gradient(135deg, ${TIER_COLORS[notif.tier]}55, ${TIER_COLORS[notif.tier]}22)`,
                                borderColor: TIER_COLORS[notif.tier]
                            }}
                        >
                            🎉
                        </div>
                        <div className="text-left">
                            <p className="text-pit-green font-black uppercase italic tracking-tighter text-sm mb-1">Achievement Unlocked</p>
                            <h4 className="text-white text-xl font-black italic uppercase leading-none">
                                {notif.achievementName} – <span style={{ color: TIER_COLORS[notif.tier] }}>{TIER_METALS[notif.tier]} Tier</span>
                            </h4>
                            <p className="text-slate-400 text-[10px] font-black uppercase tracking-widest mt-2">
                                {notif.milestone.toLocaleString()} {notif.achievementName.includes('Wins') || notif.achievementName.includes('Slayer') ? 'Wins' : notif.achievementName.includes('Time') ? 'Seconds' : notif.achievementName.includes('Ascension') ? 'Level' : 'Achieved'}
                            </p>
                        </div>
                    </motion.div>
                ))}
            </AnimatePresence>
        </div>
    );
}
